import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Bot } from "lucide-react";
import { API } from "@/lib/api";

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm Vectra Copilot. Ask me to craft a prompt, explain a studio, or plan a pipeline." },
  ]);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1].content += payload.delta;
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1].content = "Connection error. Try again.";
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="floating-ai-chat-button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-3 px-5 py-4 rounded-full bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 text-white border border-purple-400/40 shadow-[0_0_30px_rgba(139,92,246,0.5)] hover:scale-105 transition-transform"
      >
        <Sparkles size={20} />
        <span className="hidden sm:block font-semibold text-sm">Vectra Copilot</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-drawer"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 left-6 z-50 w-[92vw] sm:w-96 h-[520px] max-h-[75vh] glass rounded-2xl flex flex-col overflow-hidden border border-purple-500/20"
          >
            <div className="flex items-center justify-between px-4 h-14 border-b border-white/10 bg-gradient-to-r from-purple-600/15 to-cyan-500/10">
              <div className="flex items-center gap-2">
                <Bot size={18} className="text-cyan-400" />
                <span className="font-display font-semibold text-sm">Vectra AI Copilot</span>
              </div>
              <button data-testid="close-chat-button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                        : "bg-white/5 text-slate-200 border border-white/10"
                    }`}
                  >
                    {m.content || <span className="inline-block w-2 h-4 bg-cyan-400/70 animate-pulse rounded-sm" />}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-white/10">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask Vectra Copilot..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-600"
                />
                <button
                  data-testid="chat-send-button"
                  onClick={send}
                  disabled={streaming}
                  className="text-cyan-400 hover:text-cyan-300 disabled:opacity-40 transition-colors"
                >
                  <Send size={17} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
